import {
  Box,
  Container,
  Typography,
  Skeleton,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton,
  CircularProgress
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import ProductCard from "../../Components/ProductCard/ProductCard";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import Filter from "../../Components/Filter/Filter";
import { postData, getData } from "../../Webservice/Webservice";
import NotFound from "../NotFound/NotFound";
import { getIsBusiness } from "../../Utils/authHelper";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

function ProductList() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const { categoryId: stateCategoryId, subcategoryId: stateSubcategoryId, searchTerm: stateSearchTerm, layoutId: stateLayoutId, tabId: stateTabId, categoryTitle: stateTitle, subcategoryTitle: stateSubcategoryTitle } = location.state || {};
  const [searchParams] = useSearchParams();
  const { category, subcategory } = useParams();

  const categoryId = searchParams.get("categoryId") || stateCategoryId;
  const subcategoryId = searchParams.get("subcategoryId") || stateSubcategoryId;
  const searchTerm = searchParams.get("searchTerm") || stateSearchTerm;
  const layoutId = searchParams.get("layoutId") || stateLayoutId;
  const tabId = searchParams.get("tabId") || stateTabId;
  const title = searchParams.get("categoryTitle") || searchParams.get("title") || stateTitle || (category ? category.replace(/-/g, ' ') : "");
  const subcategoryTitle = searchParams.get("subcategoryTitle") || stateSubcategoryTitle || (subcategory ? subcategory.replace(/-/g, ' ') : "");
  const urlBrand = searchParams.get("brand");
  const urlBrands = urlBrand ? [urlBrand] : [];
  const urlMaxPrice = searchParams.get("maxPrice") ? parseInt(searchParams.get("maxPrice")) : null;
  const urlMinPrice = searchParams.get("minPrice") ? parseInt(searchParams.get("minPrice")) : null;

  const [filterOpen, setFilterOpen] = useState(false);
  // If URL has explicit price params (e.g. from Shop By Price), honour them as initial values
  const hasUrlPriceFilter = urlMinPrice !== null || urlMaxPrice !== null;
  const [priceRange, setPriceRange] = useState([
    urlMinPrice ?? 0,
    urlMaxPrice ?? 5000
  ]);
  const [selectedBrands, setSelectedBrands] = useState(urlBrands);
  const [selectedIdealFor, setSelectedIdealFor] = useState("all");
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedUnitType, setSelectedUnitType] = useState([]);
  const [codAvailable, setCodAvailable] = useState("all");
  const [selectedSkinTypes, setSelectedSkinTypes] = useState([]);
  const [selectedHairTypes, setSelectedHairTypes] = useState([]);
  const [isVegan, setIsVegan] = useState(false);
  const [isCrueltyFree, setIsCrueltyFree] = useState(false);
  const [isOrganic, setIsOrganic] = useState(false);
  const [includeOutOfStock, setIncludeOutOfStock] = useState(false);

  const [products, setProducts] = useState([]);
  const [allProductsForContext, setAllProductsForContext] = useState([]); // Master list for filter options
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleApplyFilters = () => {
    setFilterOpen(false);
  };

  const mapProductData = (productList) => {
    if (productList && Array.isArray(productList)) {
      const isBusiness = getIsBusiness();
      return productList.map((p) => ({
        img: p.product_images?.[0] || "https://via.placeholder.com/300",
        category: p.category_id?.title || "",
        name: p.title,
        price: isBusiness ? `₹${p.b2b_sellingPrice || 0}` : `₹${p.b2c_sellingPrice || 0}`,
        brand: p.brand || "",
        idealFor: p.ideal_for || "",
        color: p.color || "",
        id: p._id,
        mrp: isBusiness ? (p.b2b_mrp || p.b2c_mrp || 0) : (p.b2c_mrp || 0),
        sellingPriceNum: isBusiness ? (p.b2b_sellingPrice || 0) : (p.b2c_sellingPrice || 0),
        b2b_discountValue: p.b2b_discountValue,
        b2b_discountType: p.b2b_discountType,
        b2c_sellingPrice: p.b2c_sellingPrice,
        b2b_sellingPrice: p.b2b_sellingPrice,
        b2c_mrp: p.b2c_mrp,
        b2b_mrp: p.b2b_mrp,
        skin_type: p.skin_type || "",
        hair_type: p.hair_type || "",
        is_vegan: !!p.is_vegan,
        cruelty_free: !!p.cruelty_free,
        organic: !!p.organic,
        codAvailable: !!p.codAvailable,
        inStock: !!p.inStock,
        unitType: p.unitType || "",
      }));
    }
    return [];
  };

  const fetchContextProducts = async () => {
    setLoading(true);
    try {
      const tokenValue = localStorage.getItem("website_jwt_token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      const params = new URLSearchParams();
      if (subcategoryId) {
        params.append("subcategory_id", subcategoryId);
        if (categoryId) params.append("category_id", categoryId);
      } else if (subcategory) {
        params.append("subcategory_slug", subcategory);
        if (category) params.append("category_slug", category);
      } else if (categoryId) {
        params.append("category_id", categoryId);
      } else if (category) {
        params.append("category_slug", category);
      }
      
      if (searchTerm) params.append("search", searchTerm);
      if (layoutId) params.append("layout_id", layoutId);
      if (tabId) params.append("tab_id", tabId);

      // No range or category-specific filters here, we want EVERYTHING for this context
      const response = await postData(`product/list?${params.toString()}&limit=1000`, {}, token);

      let productList = (Array.isArray(response.results) && response.results.length > 0) ? response.results : 
                        (Array.isArray(response.products) && response.products.length > 0) ? response.products : 
                        (response.product_id || []);
      const mapped = mapProductData(productList);
      setAllProductsForContext(mapped);
    } catch (error) {
      console.error("Error fetching context products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    return allProductsForContext.filter(p => {
      if (selectedBrands.length > 0 && !selectedBrands.some(b => b?.toLowerCase() === p.brand?.toLowerCase())) return false;
      if (selectedIdealFor && selectedIdealFor !== "all" && p.idealFor !== selectedIdealFor) return false;
      if (selectedUnitType.length > 0 && !selectedUnitType.includes(p.unitType)) return false;
      if (codAvailable && codAvailable !== "all" && String(p.codAvailable) !== (codAvailable === "yes" ? "true" : "false")) return false;
      if (selectedSkinTypes.length > 0 && !selectedSkinTypes.includes(p.skin_type)) return false;
      if (selectedHairTypes.length > 0 && !selectedHairTypes.includes(p.hair_type)) return false;
      if (isVegan && !p.is_vegan) return false;
      if (isCrueltyFree && !p.cruelty_free) return false;
      if (isOrganic && !p.organic) return false;
      if (!includeOutOfStock && p.inStock === false) return false;
      if (p.sellingPriceNum < priceRange[0] || p.sellingPriceNum > priceRange[1]) return false;
      return true;
    });
  }, [allProductsForContext, selectedBrands, selectedIdealFor, selectedUnitType, codAvailable, selectedSkinTypes, selectedHairTypes, isVegan, isCrueltyFree, isOrganic, priceRange, includeOutOfStock]);

  useEffect(() => {
    setProducts(filteredProducts.slice(0, page * 20));
    setTotalProducts(filteredProducts.length);
    setHasMore(page * 20 < filteredProducts.length);
  }, [filteredProducts, page]);

  useEffect(() => {
    if (categoryId || subcategoryId || category || subcategory || searchTerm || layoutId || tabId || urlMaxPrice || urlMinPrice || urlBrand) {
      fetchContextProducts();
    }
  }, [categoryId, subcategoryId, category, subcategory, searchTerm, layoutId, tabId, urlMaxPrice, urlMinPrice, urlBrand]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedBrands, selectedIdealFor, selectedUnitType, codAvailable, selectedSkinTypes, selectedHairTypes, isVegan, isCrueltyFree, isOrganic, priceRange, includeOutOfStock]);

  // Infinite scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.documentElement.offsetHeight - 500 &&
        hasMore &&
        !loading &&
        !loadingMore
      ) {
        setPage(prev => prev + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading, loadingMore]);

  useEffect(() => {
    if (urlBrand) {
      setSelectedBrands([urlBrand]);
    }
  }, [urlBrand]);

  // ---- Derive all filter options from the MASTER context products ----
  const availableBrands = [...new Set(
    allProductsForContext.map((p) => p.brand).filter(Boolean)
  )].sort();

  const availableIdealFor = [...new Set(
    allProductsForContext.map((p) => p.idealFor).filter(Boolean)
  )].sort();

  const availableSkinTypes = [...new Set(
    allProductsForContext.map((p) => p.skin_type).filter(Boolean)
  )].sort();

  const availableHairTypes = [...new Set(
    allProductsForContext.map((p) => p.hair_type).filter(Boolean)
  )].sort();

  // Trust badges: based on ALL potential products in this context
  const hasSomeVegan = allProductsForContext.some((p) => p.is_vegan);
  const hasSomeCrueltyFree = allProductsForContext.some((p) => p.cruelty_free);
  const hasSomeOrganic = allProductsForContext.some((p) => p.organic);
  const hasSomeCOD = allProductsForContext.some((p) => p.codAvailable === true);

  // Price range baseline from MASTER products
  const productPrices = allProductsForContext.map((p) => p.sellingPriceNum || 0).filter((v) => v > 0);
  
  const computedMinPrice = productPrices.length > 0
    ? Math.floor(Math.min(...productPrices))
    : 0;
    
  const computedMaxPrice = productPrices.length > 0
    ? Math.max(Math.ceil(Math.max(...productPrices)), 5000)
    : 5000;

  useEffect(() => {
    if (productPrices.length > 0) {
      if (!hasUrlPriceFilter) {
        setPriceRange([computedMinPrice, computedMaxPrice]);
      } else {
        setPriceRange([
          urlMinPrice !== null ? Math.max(urlMinPrice, computedMinPrice) : computedMinPrice,
          urlMaxPrice !== null ? Math.min(urlMaxPrice, computedMaxPrice) : computedMaxPrice
        ]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedMinPrice, computedMaxPrice, urlMinPrice, urlMaxPrice, hasUrlPriceFilter]);

  const colors = [...new Set(allProductsForContext.map((p) => p.color).filter(Boolean))];

  const clearAllFilters = () => {
    setPriceRange([computedMinPrice, computedMaxPrice]);
    setSelectedBrands([]);
    setSelectedIdealFor("all");
    setSelectedColors([]);
    setSelectedUnitType([]);
    setCodAvailable("all");
    setSelectedSkinTypes([]);
    setSelectedHairTypes([]);
    setIsVegan(false);
    setIsCrueltyFree(false);
    setIsOrganic(false);
    setIncludeOutOfStock(false);
  };

  if (!loading && !categoryId && !subcategoryId && !category && !subcategory && !searchTerm && !layoutId && !tabId && !hasUrlPriceFilter && !urlBrand) {
    return <NotFound />;
  }

  const PageHeader = (
    <Box sx={{ mb: { xs: 4, md: 6 } }}>
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: "24px", sm: "32px", md: "40px" },
          color: "#222",
          ...rubikfont,
          fontWeight: 500,
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          mb: 1
        }}
      >
        {urlBrand 
          ? `BRAND: ${urlBrand.toUpperCase()}`
          : (searchTerm 
              ? `SEARCH RESULTS FOR "${searchTerm.toUpperCase()}"` 
              : (subcategoryTitle 
                  ? subcategoryTitle.toUpperCase() 
                  : (title ? title.toUpperCase() : "PRODUCT LIST")))}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ width: "40px", height: "3px", bgcolor: "#C8A054", borderRadius: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {totalProducts > 0 ? totalProducts : products.length} products found
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 } }}>
      {PageHeader}

      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 4, md: 6 } }}>
        {/* Mobile Filter Toggle (Replacement for button) */}
        {isMobile && (
          <Box 
            onClick={() => setFilterOpen(!filterOpen)}
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "space-between",
              p: 2, 
              bgcolor: "#fff", 
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              cursor: "pointer"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <FilterListIcon sx={{ color: "#C8A054" }} />
              <Typography sx={{ fontWeight: 500, ...rubikfont, fontSize: "14px" }}>REFINE SEARCH</Typography>
            </Box>
            <Typography sx={{ fontSize: "12px", color: "#999", fontWeight: 500 }}>TAP TO EDIT</Typography>
          </Box>
        )}

        {/* Sidebar Filter - Desktop / Mobile Drawer Container */}
        <Box sx={{ width: { md: "280px" }, flexShrink: 0 }}>
          {isMobile ? (
            <Filter
              variant="drawer"
              open={filterOpen}
              onClose={() => setFilterOpen(false)}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              computedMinPrice={computedMinPrice}
              computedMaxPrice={computedMaxPrice}
              selectedBrands={selectedBrands}
              setSelectedBrands={setSelectedBrands}
              selectedIdealFor={selectedIdealFor}
              setSelectedIdealFor={setSelectedIdealFor}
              brands={availableBrands}
              idealForOptions={availableIdealFor}
              skinTypeOptions={availableSkinTypes}
              hairTypeOptions={availableHairTypes}
              hasSomeVegan={hasSomeVegan}
              hasSomeCrueltyFree={hasSomeCrueltyFree}
              hasSomeOrganic={hasSomeOrganic}
              hasSomeCOD={hasSomeCOD}
              onClearFilters={clearAllFilters}
              selectedUnitType={selectedUnitType}
              setSelectedUnitType={setSelectedUnitType}
              selectedSkinTypes={selectedSkinTypes}
              setSelectedSkinTypes={setSelectedSkinTypes}
              selectedHairTypes={selectedHairTypes}
              setSelectedHairTypes={setSelectedHairTypes}
              isVegan={isVegan}
              setIsVegan={setIsVegan}
              isCrueltyFree={isCrueltyFree}
              setIsCrueltyFree={setIsCrueltyFree}
              isOrganic={isOrganic}
              setIsOrganic={setIsOrganic}
              includeOutOfStock={includeOutOfStock}
              setIncludeOutOfStock={setIncludeOutOfStock}
              codAvailable={codAvailable}
              setCodAvailable={setCodAvailable}
              onApply={handleApplyFilters}
            />
          ) : (
            <Box sx={{ position: "sticky", top: "100px" }}>
              <Filter
                variant="static"
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                computedMinPrice={computedMinPrice}
                computedMaxPrice={computedMaxPrice}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                selectedIdealFor={selectedIdealFor}
                setSelectedIdealFor={setSelectedIdealFor}
                brands={availableBrands}
                idealForOptions={availableIdealFor}
                skinTypeOptions={availableSkinTypes}
                hairTypeOptions={availableHairTypes}
                hasSomeVegan={hasSomeVegan}
                hasSomeCrueltyFree={hasSomeCrueltyFree}
                hasSomeOrganic={hasSomeOrganic}
                hasSomeCOD={hasSomeCOD}
                onClearFilters={clearAllFilters}
                selectedUnitType={selectedUnitType}
                setSelectedUnitType={setSelectedUnitType}
                selectedSkinTypes={selectedSkinTypes}
                setSelectedSkinTypes={setSelectedSkinTypes}
                selectedHairTypes={selectedHairTypes}
                setSelectedHairTypes={setSelectedHairTypes}
                isVegan={isVegan}
                setIsVegan={setIsVegan}
                isCrueltyFree={isCrueltyFree}
                setIsCrueltyFree={setIsCrueltyFree}
                isOrganic={isOrganic}
                setIsOrganic={setIsOrganic}
                includeOutOfStock={includeOutOfStock}
                setIncludeOutOfStock={setIncludeOutOfStock}
                codAvailable={codAvailable}
                setCodAvailable={setCodAvailable}
                onApply={handleApplyFilters}
              />
            </Box>
          )}
        </Box>

        {/* Product Grid Area */}
        <Box sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
                xl: "repeat(4, 1fr)",
              },
              gap: { xs: 2, sm: 3, md: 4 },
            }}
          >
            {loading ? (
              [...Array(8)].map((_, index) => (
                <Box key={index} sx={{ bgcolor: "#fff", borderRadius: 2, p: 1 }}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 1.5 }} />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </Box>
              ))
            ) : (
              products.map((product) => (
                <ProductCard key={product.id} product={{ ...product, _id: product.id }} variant="listing" />
              ))
            )}
          </Box>
          
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, mt: 2 }}>
              <CircularProgress sx={{ color: "#C8A054" }} />
            </Box>
          )}
          
          {!loading && !loadingMore && !hasMore && products.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, mt: 2 }}>
              <Typography sx={{ color: '#888', fontWeight: 500, fontSize: '14px', ...rubikfont }}>
                No more products to show
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}

export default ProductList;
